import { useState } from 'react'
import { useI18n } from '../../i18n'
import { useSongStore } from '../../store/useSongStore'

interface NewSongDialogProps {
  onCreated: (songId: string) => void
  onClose: () => void
  // When the dialog is opened from inside a specific collection view,
  // preselect that collection so the song actually lands there.
  defaultCollectionId?: string
}

export function NewSongDialog({ onCreated, onClose, defaultCollectionId }: NewSongDialogProps) {
  const { t } = useI18n()
  const { collections, createSong, createCollection } = useSongStore()

  const hasDefault = !!defaultCollectionId && collections.some((c) => c.id === defaultCollectionId)

  const [mode, setMode] = useState<'select' | 'new'>(
    collections.length > 0 ? 'select' : 'new'
  )
  const [selectedCollectionId, setSelectedCollectionId] = useState(
    hasDefault ? defaultCollectionId! : collections[0]?.id ?? ''
  )
  const [songTitle, setSongTitle] = useState('')
  const [newCollectionName, setNewCollectionName] = useState('')
  const [newCollectionArtist, setNewCollectionArtist] = useState('')
  const [isCreating, setIsCreating] = useState(false)

  const handleCreate = async () => {
    setIsCreating(true)

    let collectionId: string | null = null

    if (mode === 'new') {
      const name = newCollectionName.trim()
      if (!name) {
        setIsCreating(false)
        return
      }
      collectionId = await createCollection(name, newCollectionArtist.trim())
      if (!collectionId) {
        setIsCreating(false)
        return
      }
    } else {
      collectionId = selectedCollectionId
    }

    if (!collectionId) {
      setIsCreating(false)
      return
    }

    const songId = await createSong(songTitle.trim(), collectionId)
    setIsCreating(false)
    if (songId) onCreated(songId)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center sm:justify-center" onClick={onClose}>
      <div
        className="w-full rounded-t-2xl border-t border-surface-lighter bg-surface-light p-5 pb-8 sm:max-w-md sm:rounded-2xl sm:border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-surface-lighter sm:hidden" />

        <h3 className="mb-4 text-lg font-bold text-text">{t('songbook.newSong')}</h3>

        {/* Song title */}
        <input
          type="text"
          value={songTitle}
          onChange={(e) => setSongTitle(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !isCreating && handleCreate()}
          placeholder={t('songbook.songTitle')}
          autoFocus
          className="mb-4 w-full rounded-lg border-2 border-primary bg-surface px-3 py-2.5 text-text placeholder-text-muted focus:outline-none"
        />

        {/* Choose existing collection */}
        {collections.length > 0 && (
          <label
            className={`mb-3 flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
              mode === 'select' ? 'border-primary bg-primary/10' : 'border-surface-lighter'
            }`}
            onClick={() => setMode('select')}
          >
            <input
              type="radio"
              checked={mode === 'select'}
              onChange={() => setMode('select')}
              className="mt-1 accent-primary"
            />
            <div className="flex-1">
              <p className="font-medium text-text">{t('songbook.addToCollection')}</p>
              <select
                value={selectedCollectionId}
                onChange={(e) => {
                  setSelectedCollectionId(e.target.value)
                  setMode('select')
                }}
                className="mt-2 w-full rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2 text-text focus:border-primary focus:outline-none"
              >
                {collections.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.artist ? ` — ${c.artist}` : ''}
                  </option>
                ))}
              </select>
            </div>
          </label>
        )}

        {/* New collection */}
        <div
          className={`mb-3 flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 transition-colors ${
            mode === 'new' ? 'border-primary bg-primary/10' : 'border-surface-lighter'
          }`}
          onClick={() => setMode('new')}
        >
          <input
            type="radio"
            checked={mode === 'new'}
            onChange={() => setMode('new')}
            className="mt-1 accent-primary"
          />
          <div className="flex-1">
            <p className="font-medium text-text">{t('songbook.newCollectionStart')}</p>
            <input
              type="text"
              value={newCollectionName}
              onChange={(e) => {
                setNewCollectionName(e.target.value)
                setMode('new')
              }}
              placeholder={t('songbook.collectionName')}
              className="mt-2 w-full rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2 text-text placeholder-text-muted focus:border-primary focus:outline-none"
            />
            <input
              type="text"
              value={newCollectionArtist}
              onChange={(e) => {
                setNewCollectionArtist(e.target.value)
                setMode('new')
              }}
              placeholder={t('songbook.artistPlaceholder')}
              className="mt-2 w-full rounded-lg border-2 border-surface-lighter bg-surface px-3 py-2 text-text placeholder-text-muted focus:border-primary focus:outline-none"
            />
          </div>
        </div>

        <p className="mb-4 text-xs text-text-muted">{t('songbook.collectionHint')}</p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            className="flex-1 rounded-lg border-2 border-surface-lighter py-2.5 font-medium text-text-muted active:bg-surface-lighter"
            onClick={onClose}
          >
            {t('general.cancel')}
          </button>
          <button
            className="flex-1 rounded-lg bg-primary py-2.5 font-semibold text-white active:bg-primary-dark disabled:opacity-50"
            onClick={handleCreate}
            disabled={isCreating || (mode === 'new' && !newCollectionName.trim())}
          >
            {isCreating ? t('general.loading') : t('songbook.newSong')}
          </button>
        </div>
      </div>
    </div>
  )
}
